import { useQuery, useMutation } from "convex/react";
import { api } from "@convex/_generated/api";
import { Link, useParams, useSearchParams } from "react-router";
import { Pencil, Plus, RotateCcw, UserX } from "lucide-react";
import type { Id } from "@convex/_generated/dataModel";
import {
  Breadcrumb,
  Button,
  PageContainer,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@repo/ui";
import { arianeOrganisation, libelleSection } from "../../lib/ariane";

export function UsersList() {
  const { id: orgId } = useParams<{ id: string }>();
  const [search] = useSearchParams();
  const filtreGroupeId = search.get("groupId");

  const users = useQuery(
    api.superAdmin.listerUsersOrg,
    orgId ? { organisationId: orgId as Id<"organisations"> } : "skip",
  );
  const groupes = useQuery(
    api.superAdmin.listerGroupes,
    orgId ? { organisationId: orgId as Id<"organisations"> } : "skip",
  );
  const org = useQuery(
    api.superAdmin.obtenirOrganisation,
    orgId ? { organisationId: orgId as Id<"organisations"> } : "skip",
  );
  const changerGroupe = useMutation(api.superAdmin.changerGroupeUser);
  const desactiver = useMutation(api.superAdmin.desactiverUser);
  const reactiver = useMutation(api.superAdmin.reactiverUser);
  const desactives = useQuery(
    api.superAdmin.listerUsersDesactivesOrg,
    orgId ? { organisationId: orgId as Id<"organisations"> } : "skip",
  );

  const filteredUsers = filtreGroupeId
    ? users?.filter((u: any) => u.groupId === filtreGroupeId)
    : users;

  const getGroupeNom = (groupId?: string) => {
    if (!groupId) return "Sans groupe";
    return groupes?.find((g: any) => g._id === groupId)?.nom ?? groupId;
  };

  if (users === undefined) {
    return <div className="flex justify-center p-16"><div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>;
  }

  return (
    <PageContainer>
      <Breadcrumb items={arianeOrganisation(orgId, org?.nom, "users")} />
      <PageHeader
        titre={libelleSection("users")}
        sousTitre={`${filteredUsers?.length ?? 0} utilisateur(s)`}
        actions={
          <Button asChild>
            <Link to={`/organisations/${orgId}/users/creer`}>
              <Plus size={14} />
              Nouvel utilisateur
            </Link>
          </Button>
        }
      />

      <div className="bg-card border border-border rounded-surface overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-accent/5 border-b border-border">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Email</th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Groupe</th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Rôle</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y">
            {filteredUsers?.map((u: any) => (
              <tr key={u._id} className="transition-colors hover:bg-charte-hover">
                <td className="px-4 py-3">{u.email ?? "—"}</td>
                <td className="px-4 py-3">
                  {u.groupId ? (
                    groupes ? (
                      <Select
                        value={u.groupId}
                        onValueChange={(gId) => {
                          if (gId) void changerGroupe({ userId: u._id, groupId: gId as Id<"groups"> });
                        }}
                      >
                        <SelectTrigger aria-label="Groupe" className="w-auto min-w-40">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {groupes.map((g: any) => (
                            <SelectItem key={g._id} value={g._id}>
                              {g.nom}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      getGroupeNom(u.groupId)
                    )
                  ) : (
                    <span className="text-muted-foreground text-sm">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {u.isAdmin ? (
                    <span className="bg-primary/10 text-primary text-xs px-2 py-0.5 rounded-full">Super-admin</span>
                  ) : !u.groupId ? (
                    <span
                      className="bg-blue-500/10 text-blue-600 text-xs px-2 py-0.5 rounded-full"
                      title="Sans groupe assigné : accès à tous les parcours de l'organisation."
                    >
                      Gestionnaire
                    </span>
                  ) : (
                    <span className="bg-accent/10 text-muted-foreground text-xs px-2 py-0.5 rounded-full">Utilisateur</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <Link
                    to={`/organisations/${orgId}/users/${u._id}/modifier`}
                    className="inline-block text-muted-foreground hover:text-foreground transition-colors mr-3"
                    title="Modifier"
                    aria-label={`Modifier ${u.email ?? ""}`}
                  >
                    <Pencil size={14} />
                  </Link>
                  <button
                    onClick={() => {
                      if (confirm(`Désactiver ${u.email} ?`)) void desactiver({ userId: u._id });
                    }}
                    className="text-destructive hover:text-destructive/90 transition-colors"
                    title="Désactiver"
                  >
                    <UserX size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredUsers?.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">Aucun utilisateur.</div>
        )}
      </div>

      {desactives && desactives.length > 0 && (
        <div className="mt-8">
          <h2 className="text-lg font-semibold mb-3">Comptes désactivés</h2>
          <div className="bg-card border border-border rounded-surface overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-accent/5 border-b border-border">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Email</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Ancien groupe</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Désactivé le</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y">
                {desactives.map((u) => (
                  <tr key={u._id} className="text-muted-foreground">
                    <td className="px-4 py-3">{u.email ?? "—"}</td>
                    <td className="px-4 py-3">{u.groupId ? getGroupeNom(u.groupId) : "Gestionnaire"}</td>
                    <td className="px-4 py-3">{new Date(u.desactiveLe).toLocaleDateString("fr-FR")}</td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          reactiver({ userId: u._id }).catch((err: unknown) =>
                            alert(err instanceof Error ? err.message : "Erreur inconnue"),
                          );
                        }}
                      >
                        <RotateCcw size={12} />
                        Réactiver
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </PageContainer>
  );
}
